import React from 'react';
import {
	PermissionsContext,
	DATA_PERMISSIONS,
	APVENDORDB_PERMISSIONS,
} from 'contexts/PermissionsContext';
import { UploadStateContext } from 'contexts/UploadStateContext';
import { FileArchiveContextProvider } from 'contexts/FileArchiveContext/FileArchiveContextProvider';
import { Wrapper, Content, LoaderWrapper, InfoText } from './Data.styles';
import {
	useLocation,
	NavLink,
	Route,
	Redirect,
	Switch,
} from 'react-router-dom';
import { CONFIG } from './routes';
import {
	ApButtonTab,
	ApMenu,
	ApMenuItem,
	ApSecondaryNav,
	colors,
} from '@alixpartners/ui-components';
import OverwriteSpendCube from './OverwriteSpendCube';
import DownloadRawDataSection from './DownloadData';
import AppendData from './AppendData';
import VendorCleanup from './VendorCleanup';
import { VendorFileArchiveContextProvider } from 'contexts/VendorFileArchiveContext/VendorFileArchiveContextProvider';
import { VendorFileArchiveContext } from 'contexts/VendorFileArchiveContext';
import PushBenchmark from './BenchmarkDB/PushBenchmark';
import VendorEdit from './VendorEdit/VendorEdit';
import { UploadVendorStateContext } from 'contexts/UploadVendorStateContext';

import { ApSignalrConsumer } from '@alixpartners/ui-utils';
import { useHistory } from 'react-router-dom';

import PushToApVendorDB from './ApVendorDB/PushToApVendorDB';
import UpdateApVendorDB from './ApVendorDB/UpdateApVendorDB/UpdateApVendorDB';
import { ToastMessageProvider } from 'contexts/ToastMessageContext/ToastMessageProvider';
import { ClassificationThresholdProvider } from 'contexts/APVendorDBContext/ClassificationThresholdProvider';
import VendorMapping from './VendorMapping/VendorMapping';
const ContentComponent = {
	vendorCleanup: VendorCleanup,
	append: AppendData,
	vendorEdit: VendorEdit,
	upload: OverwriteSpendCube,
	download: DownloadRawDataSection,
	pushBenchmark: PushBenchmark,
	pushToApVendorDB: PushToApVendorDB,
	updateApVendorDB: UpdateApVendorDB,
	vendorMapping: VendorMapping,
};
const FIRST_UPLOAD_ROUTE = 'vendorCleanup';

export const Data = ({
	permissions,
	status,
	vendorStatus,
	refetchNormalization,
	normalizationUploadStatus,
}) => {
	const location = useLocation();
	const history = useHistory();
	const isFirstUpload = !status.isDataPublished;
	const hasSomePermissions = requiredSome => {
		return !requiredSome || requiredSome.some(i => permissions.includes(i));
	};
	const noPermissions = !hasSomePermissions(DATA_PERMISSIONS);

	// Fallback if none of the data permissions are granted
	if (noPermissions) {
		return (
			<Wrapper>
				<LoaderWrapper>
					<InfoText>You don't have permissions to view this page</InfoText>
				</LoaderWrapper>
			</Wrapper>
		);
	}
	const isSubNavDisable = name => {
		let flag = true;
		if (name === 'append') {
			flag = false;
		} else if (name === 'vendorEdit') {
			flag =
				vendorStatus.state === 1 && normalizationUploadStatus === 0
					? false
					: true;
		} else {
			flag = name !== FIRST_UPLOAD_ROUTE && isFirstUpload;
		}

		return flag;
	};
	return (
		<Wrapper>
			<ApSignalrConsumer
				message="VendorIntegration"
				onMessage={(
					messageName,
					statusMessage,
					currentUploadStep,
					totalUploadSteps
				) => {
					refetchNormalization();
					currentUploadStep === '1' && history.push('/data/vendor-cleanup');
				}}
			/>
			<ApSecondaryNav
				left={
					<React.Fragment>
						{CONFIG.map(tab => {
							if ('path' in tab) {
								const { label, path, name, requiredSome } = tab;
								if (requiredSome && !hasSomePermissions(requiredSome)) {
									return null;
								}
								console.log(requiredSome);
								return (
									<NavLink to={path} key={path}>
										<ApButtonTab
											isSelected={location.pathname.includes(path)}
											disabled={isSubNavDisable(name)}
										>
											{label}
										</ApButtonTab>
									</NavLink>
								);
							} else {
								if (tab.requiredSome && !hasSomePermissions(tab.requiredSome)) {
									return null;
								}
								return (
									<ApMenu
										key={tab.label}
										toggleVariant="hover"
										button={
											<ApButtonTab
												toggleVariant="hover"
												isSelected={tab.child.some(
													child =>
														child.path === location.pathname ||
														location.pathname.includes(child.path)
												)}
											>
												{tab.label}
											</ApButtonTab>
										}
									>
										{tab.child.map(({ label, path, name, requiredSome }) => {
											if (requiredSome && !hasSomePermissions(requiredSome)) {
												return null;
											}
											return (
												<ApMenuItem
													key={label}
													style={{
														backgroundColor:
															path === location.pathname ||
															location.pathname.includes(path)
																? colors.green0
																: null,
													}}
												>
													<NavLink to={path} key={path}>
														{label}
													</NavLink>
												</ApMenuItem>
											);
										})}
									</ApMenu>
								);
							}
						})}
					</React.Fragment>
				}
			/>

			<Content>
				<Switch>
					{CONFIG.flatMap(({ child }) => child).map(
						({ path, name, requiredSome }) => {
							if (!hasSomePermissions(requiredSome)) {
								return null;
							}

							return (
								<Route
									key={path}
									path={path}
									component={ContentComponent[name]}
								/>
							);
						}
					)}

					<Redirect
						to={
							!isFirstUpload
								? (() => {
										for (const { child } of CONFIG) {
											for (const { path, requiredSome } of child) {
												if (hasSomePermissions(requiredSome)) {
													return path;
												}
											}
										}
										return CONFIG[0].child[0].path;
								  })()
								: CONFIG[0].child[0].path
						}
					/>
				</Switch>
			</Content>
		</Wrapper>
	);
};

export default props => (
	<FileArchiveContextProvider>
		<VendorFileArchiveContextProvider>
			<VendorFileArchiveContext.Consumer>
				{({ fileArchive, state }) => (
					<UploadVendorStateContext.Consumer>
						{({ vendorStatus }) => (
							<UploadStateContext.Consumer>
								{({ status, normalizationUploadStatus, refetch }) => (
									<PermissionsContext.Consumer>
										{({ permissions }) => (
											<ToastMessageProvider>
												{permissions.some(permission =>
													APVENDORDB_PERMISSIONS.includes(permission)
												) ? (
													<ClassificationThresholdProvider>
														<Data
															{...props}
															permissions={permissions || []}
															status={status}
															normalizationUploadStatus={
																normalizationUploadStatus
															}
															state={state}
															vendorStatus={vendorStatus}
															fileArchive={fileArchive}
															refetchNormalization={refetch}
														/>
													</ClassificationThresholdProvider>
												) : (
													<Data
														{...props}
														permissions={permissions || []}
														status={status}
														normalizationUploadStatus={
															normalizationUploadStatus
														}
														state={state}
														vendorStatus={vendorStatus}
														fileArchive={fileArchive}
														refetchNormalization={refetch}
													/>
												)}
											</ToastMessageProvider>
										)}
									</PermissionsContext.Consumer>
								)}
							</UploadStateContext.Consumer>
						)}
					</UploadVendorStateContext.Consumer>
				)}
			</VendorFileArchiveContext.Consumer>
		</VendorFileArchiveContextProvider>
	</FileArchiveContextProvider>
);
